import { useEffect, useState, useCallback, useRef } from 'react';
import {
    TextField, Box, Typography, Button, Chip, Stack, Alert, Skeleton,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PersonAddIcon from '@mui/icons-material/PersonAddAlt1';
import { debounce } from 'lodash';
import { Surface } from '../../components/primitives';
import { adminFetch } from '../../utils/authUtils';
import UserDetailDialog from './UserDetailDialog';
import InviteUserDialog from './InviteUserDialog';

const PAGE_SIZE = 25;

/**
 * Admin users table with cursor-based Next/Prev pagination.
 *
 * Cognito's list_users is strictly cursor-based (opaque PaginationToken) with
 * no total count and no ability to jump to an arbitrary page. We cache every
 * cursor we discover in `cursorsRef` so Prev works without re-walking from
 * the start. Search term is passed with every fetch — pagination composes
 * cleanly with server-side filtering.
 */
export default function UsersPanel() {
    const [search, setSearch] = useState('');
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [inviteOpen, setInviteOpen] = useState(false);

    // cursorsRef[i] = PaginationToken to fetch page i (0-indexed).
    // cursorsRef[0] is always null (first page needs no token).
    // Populated as we walk forward: after fetching page N and receiving
    // next_cursor, we set cursorsRef[N+1] = next_cursor.
    const cursorsRef = useRef([null]);

    // Drop stale responses when user types quickly.
    const requestIdRef = useRef(0);

    const fetchPage = useCallback(async (pageIdx, query) => {
        const myId = ++requestIdRef.current;
        setLoading(true);
        setError(null);
        try {
            const data = await adminFetch('/admin/users/list', {
                search: query || '',
                limit: PAGE_SIZE,
                cursor: cursorsRef.current[pageIdx] || undefined,
            });
            if (myId !== requestIdRef.current) return;
            setUsers(data.users || []);
            setHasNextPage(Boolean(data.next_cursor));
            // Remember the cursor for the next page so Next works.
            if (data.next_cursor) {
                cursorsRef.current[pageIdx + 1] = data.next_cursor;
            } else {
                // Trim any stale cursors past the new end-of-list.
                cursorsRef.current = cursorsRef.current.slice(0, pageIdx + 1);
            }
        } catch (e) {
            if (myId === requestIdRef.current) setError(e.message);
        } finally {
            if (myId === requestIdRef.current) setLoading(false);
        }
    }, []);

    /** Reset cursor cache and fetch page 0 — called on mount, refresh, and search change. */
    const resetAndFetch = useCallback((query) => {
        cursorsRef.current = [null];
        setPage(0);
        fetchPage(0, query);
    }, [fetchPage]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(debounce((q) => resetAndFetch(q), 400), [resetAndFetch]);

    useEffect(() => { resetAndFetch(''); }, [resetAndFetch]);

    const onSearchChange = (e) => {
        setSearch(e.target.value);
        debouncedSearch(e.target.value);
    };

    const handlePrev = () => {
        if (page > 0 && !loading) {
            const nextPage = page - 1;
            setPage(nextPage);
            fetchPage(nextPage, search);
        }
    };

    const handleNext = () => {
        if (hasNextPage && !loading) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchPage(nextPage, search);
        }
    };

    const refresh = () => resetAndFetch(search);

    const groupChipSx = (g) => {
        if (g === 'Admin') return { bgcolor: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.25)' };
        if (g === 'Comp') return { bgcolor: 'rgba(99,102,241,0.12)', color: 'primary.light', border: t => `1px solid ${t.palette.custom.border.subtle}` };
        if (g === 'Premium') return { bgcolor: 'custom.performance.positiveBg', color: 'success.main', border: t => `1px solid ${t.palette.custom.border.subtle}` };
        return { bgcolor: 'rgba(255,255,255,0.04)', color: 'text.secondary', border: t => `1px solid ${t.palette.custom.border.subtle}` };
    };

    return (
        <>
            <Surface sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by email (prefix match)"
                        value={search}
                        onChange={onSearchChange}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<PersonAddIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setInviteOpen(true)}
                        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                        Invite user
                    </Button>
                </Stack>
            </Surface>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Surface padding={0}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1.5fr 1fr auto',
                    px: 3, py: 1.5,
                    borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                    bgcolor: 'custom.surface.subtle',
                }}>
                    <Typography variant="overline" sx={{ color: 'text.muted' }}>Email</Typography>
                    <Typography variant="overline" sx={{ color: 'text.muted' }}>Groups</Typography>
                    <Typography variant="overline" sx={{ color: 'text.muted' }}>Status</Typography>
                    <Typography variant="overline" sx={{ color: 'text.muted' }}>&nbsp;</Typography>
                </Box>

                {loading && [...Array(5)].map((_, i) => (
                    <Box key={i} sx={{ px: 3, py: 1.5, borderBottom: t => `1px solid ${t.palette.custom.border.subtle}` }}>
                        <Skeleton width="60%" height={20} />
                    </Box>
                ))}

                {!loading && users.length === 0 && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'text.muted' }}>
                            {search ? `No users matching "${search}".` : 'No users.'}
                        </Typography>
                    </Box>
                )}

                {!loading && users.map((u) => (
                    <Box
                        key={u.username}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1.5fr 1fr auto',
                            alignItems: 'center',
                            gap: 2,
                            px: 3, py: 1.5,
                            borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                        }}
                    >
                        <Box minWidth={0}>
                            <Typography variant="body2" sx={{ color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {u.email || '—'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.muted', fontFamily: t => t.typography.fontFamilyMono }}>
                                {u.username}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {u.groups.length > 0
                                ? u.groups.map(g => (
                                    <Chip key={g} label={g} size="small" sx={groupChipSx(g)} />
                                ))
                                : <Chip label="—" size="small" sx={groupChipSx()} />
                            }
                        </Stack>
                        <Typography variant="caption" sx={{ color: 'text.muted' }}>
                            {u.status || 'UNKNOWN'}
                        </Typography>
                        <Button size="small" variant="outlined" onClick={() => setSelected(u)}>
                            Manage
                        </Button>
                    </Box>
                ))}

                {/* Pagination footer */}
                {(!loading || users.length > 0) && (
                    <Box sx={{
                        px: 3, py: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}>
                        <Typography variant="caption" sx={{ color: 'text.muted' }}>
                            Page {page + 1} · {users.length} user{users.length !== 1 ? 's' : ''}
                            {search ? ` matching "${search}"` : ''}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handlePrev}
                                disabled={page === 0 || loading}
                                startIcon={<NavigateBeforeIcon sx={{ fontSize: 16 }} />}
                            >
                                Previous
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleNext}
                                disabled={!hasNextPage || loading}
                                endIcon={<NavigateNextIcon sx={{ fontSize: 16 }} />}
                            >
                                Next
                            </Button>
                        </Stack>
                    </Box>
                )}
            </Surface>

            {selected && (
                <UserDetailDialog
                    user={selected}
                    onClose={() => setSelected(null)}
                    onChanged={() => { setSelected(null); refresh(); }}
                />
            )}

            <InviteUserDialog
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                onInvited={refresh}
            />
        </>
    );
}
